import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  Box,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Icon,
  ScrollArea,
  Spinner,
  Text,
} from "@salestrak/ui";
import { PageHeader, PageHeaderSubtitle, PageHeaderTitle } from "#app/components/page-header";
import { SettingsGroup, SettingsGroupHeader, SettingsGroupRow } from "#app/components/settings/settings-group";
import { useApiKeys, useApiKeyMutations } from "#app/hooks/settings/use-api-key-mutations";
import { useAppData } from "#app/lib/data";
import { ApiKeyActionsMenu } from "#app/components/settings/api-key-actions-menu";
import { EntityTag } from "#app/components/entity";
import { getPermissionsLabel } from "#app/components/settings/permission-badge";
import { formatDate } from "#app/lib/utils/format-date";
import { ApiKeyRevokeDialog } from "#app/components/settings/api-key-revoke-dialog";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { withPermission } from "#app/components/permissions/require-permission";

function AccessPage() {
  const { push } = useAppNavigate();
  const { currentUser } = useAppData();
  const { apiKeys, total, isLoading, error } = useApiKeys(currentUser?.userId);
  const { revokeApiKey } = useApiKeyMutations();

  const [revokingKey, setRevokingKey] = useState<{ apiKeyId: string; name: string } | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  async function handleRevoke() {
    if (!revokingKey) return;
    await revokeApiKey(revokingKey.apiKeyId);
  }

  if (isLoading && apiKeys.length === 0) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Spinner />
      </Box>
    );
  }

  if (error && apiKeys.length === 0) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Unable to load API keys</EmptyTitle>
            <EmptyDescription>{error.message}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </Box>
    );
  }

  return (
    <Box direction="column" fill="flex">
      <ScrollArea>
        <PageHeader centered>
          <PageHeaderTitle>Access & Security</PageHeaderTitle>
          <PageHeaderSubtitle>Manage API keys and access controls for your workspace.</PageHeaderSubtitle>
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
          <Box gap="xl" fill="width" maxWidth={800}>

            <SettingsGroup
              label="My API Keys"
              subtitle="Build your own integrations with the SalesTrak's GraphQL API."
            >
              <SettingsGroupHeader>
                <Text size="sm" weight="medium">
                  {total} {total === 1 ? "key" : "keys"}
                </Text>
                <Button
                  aria-label="Create API key"
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => push("/settings/access/create")}
                >
                  <Icon name="plus" size="md" />
                </Button>
              </SettingsGroupHeader>

              {apiKeys.length === 0 ? (
                <Box align="center" paddingY="xl">
                  <Text size="sm" variant="muted">No API keys yet</Text>
                </Box>
              ) : (
                apiKeys.map((key: any) => {
                  const dateLabel = key.creationDate ? formatDate(key.creationDate) : null;
                  return (
                    <SettingsGroupRow
                      key={key.apiKeyId}
                      label={key.name}
                      subtitle={`${getPermissionsLabel(key.permissions)}${dateLabel ? ` · ${dateLabel}` : ""}`}
                      leading={
                        <EntityTag
                          entity={{
                            kind: "user",
                            id: key.user?.userId ?? key.apiKeyId,
                            user: key.user ?? null,
                          }}
                          display="visual"
                          size="md"
                          interactive={false}
                        />
                      }
                      onClick={() => push(`/settings/access/${key.apiKeyId}`)}
                      actionsVisible={openMenuId === key.apiKeyId}
                    >
                      <ApiKeyActionsMenu
                        onOpenChange={(open) => setOpenMenuId(open ? key.apiKeyId : null)}
                        onEdit={() => push(`/settings/access/${key.apiKeyId}/edit`)}
                        onRevoke={() => setRevokingKey({ apiKeyId: key.apiKeyId, name: key.name })}
                      />
                    </SettingsGroupRow>
                  );
                })
              )}
            </SettingsGroup>
          </Box>
        </Box>
      </ScrollArea>

      <ApiKeyRevokeDialog
        open={revokingKey !== null}
        onOpenChange={(open) => { if (!open) setRevokingKey(null); }}
        apiKeyName={revokingKey?.name ?? ""}
        onConfirm={handleRevoke}
      />
    </Box>
  );
}

// Personal API keys — every api-key query/mutation is gated on
// `workspace.integrations`; guard the route so it isn't reachable by URL
// without that permission (mirrors the sidebar row gating).
export const Route = createFileRoute("/(app)/$workspace/settings/access/")({
  component: withPermission("workspace.integrations", AccessPage),
});
